import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Mail, KeyRound, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { getErrorMessage, TOKEN_KEY } from "../lib/api";
import Panda from "../components/Panda";
import { CurveBottom, Cloud } from "../components/Decor";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const inputCls = "mt-1.5 h-12 rounded-full px-5 border-2 border-[#c9ecf5] focus-visible:ring-[#1b8bc9]";

const Shell = ({ title, subtitle, children }) => (
  <div>
    <section className="relative bg-[#1b8bc9] overflow-hidden">
      <Cloud className="absolute left-[6%] top-8 hidden md:block cloud-drift" size={180} />
      <Cloud className="absolute right-[8%] top-16 hidden md:block cloud-drift-slow" face size={200} />
      <div className="relative max-w-5xl mx-auto px-6 pt-14 pb-28 text-center">
        <h1 className="text-white font-bold text-4xl sm:text-5xl" style={{ fontFamily: "Literata, serif" }}>{title}</h1>
        <p className="text-white/90 text-lg mt-4 max-w-xl mx-auto">{subtitle}</p>
      </div>
      <CurveBottom color="#ffffff" />
    </section>
    <section className="max-w-xl mx-auto px-6 py-10">{children}</section>
  </div>
);

export const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/auth/forgot-password", { email });
      setSent(true);
      if (r.data?.email_error) toast.error("We couldn't send the email right now. Please try again in a moment.");
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not send reset link"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="forgot-password-page">
      <Shell title="Forgot your password?" subtitle="No problem. Enter your email and we'll send a link to choose a new one.">
        <div className="bg-white rounded-3xl shadow-xl p-8">
          {sent ? (
            <div className="text-center" data-testid="forgot-sent">
              <div className="flex justify-center"><Panda size={120} accessory="bowtie" scarf="#1b8bc9" item="none" /></div>
              <CheckCircle2 className="mx-auto text-[#2e8b57] mt-2" size={36} />
              <h2 className="text-2xl font-bold text-[#0c4a6e] mt-2" style={{ fontFamily: "Literata, serif" }}>Check your inbox</h2>
              <p className="text-gray-600 mt-2">If an account exists for <span className="font-bold">{email}</span>, a reset link is on its way. It works for 60 minutes.</p>
              <p className="text-sm text-gray-500 mt-4">Didn't get it? Check spam, or <button onClick={() => setSent(false)} className="text-[#1b8bc9] font-bold hover:underline">try again</button>.</p>
              <Link to="/login" className="mt-6 inline-flex items-center gap-1 font-bold text-[#1b8bc9] hover:text-[#e5356f]"><ArrowLeft size={16} /> Back to log in</Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="forgot-form">
              <div>
                <Label htmlFor="fp-email" className="font-bold text-[#0c4a6e]">Email</Label>
                <Input id="fp-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className={inputCls} data-testid="forgot-email-input" />
              </div>
              <button type="submit" disabled={busy} className="w-full btn-pill-lg bg-[#1b8bc9] hover:bg-[#156f9f] text-white shadow-lg inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="forgot-submit">
                <Mail size={20} /> {busy ? "Sending..." : "Send reset link"}
              </button>
              <p className="text-center text-sm text-gray-500"><Link to="/login" className="text-[#1b8bc9] font-bold hover:underline">Back to log in</Link></p>
            </form>
          )}
        </div>
      </Shell>
    </div>
  );
};

export const ResetPassword = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/auth/reset-password", { token, password });
      localStorage.setItem(TOKEN_KEY, r.data.token);
      toast.success("Password updated - you're signed in!");
      window.location.assign("/lessons"); // full reload so AuthContext picks up the new token
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not reset password"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="reset-password-page">
      <Shell title="Choose a new password" subtitle="Make it at least 6 characters. You'll be signed in right after.">
        <div className="bg-white rounded-3xl shadow-xl p-8">
          {!token ? (
            <div className="text-center">
              <p className="text-gray-700">This reset link is missing its token.</p>
              <Link to="/forgot-password" className="mt-4 inline-block btn-pill bg-[#1b8bc9] text-white">Request a new link</Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
              <div>
                <Label htmlFor="rp-pass" className="font-bold text-[#0c4a6e]">New password</Label>
                <Input id="rp-pass" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="reset-password-input" />
              </div>
              <div>
                <Label htmlFor="rp-confirm" className="font-bold text-[#0c4a6e]">Confirm new password</Label>
                <Input id="rp-confirm" type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} data-testid="reset-confirm-input" />
              </div>
              <button type="submit" disabled={busy} className="w-full btn-pill-lg bg-[#e5356f] hover:bg-[#c92a5e] text-white shadow-lg inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="reset-submit">
                <KeyRound size={20} /> {busy ? "Saving..." : "Set new password"}
              </button>
              <p className="text-center text-sm text-gray-500">Link expired? <button type="button" onClick={() => navigate("/forgot-password")} className="text-[#1b8bc9] font-bold hover:underline">Request a new one</button></p>
            </form>
          )}
        </div>
      </Shell>
    </div>
  );
};
